import React from 'react'
import type { GameweekFixturesOut, FixtureLite } from '../../types'

interface GameweekFixturesProps {
  gameweek: GameweekFixturesOut | null
  onFixtureClick?: (fixture: FixtureLite) => void
}

function formatKickoffTime(dateStr: string) {
  const date = new Date(dateStr)
  return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
}

function formatKickoffDate(dateStr: string) {
  const date = new Date(dateStr)
  const now = new Date()
  const tomorrow = new Date(now)
  tomorrow.setDate(tomorrow.getDate() + 1)
  if (date.toDateString() === now.toDateString()) return 'Today'
  if (date.toDateString() === tomorrow.toDateString()) return 'Tomorrow'
  return date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })
}

function FixtureRow({ fixture, onClick }: { fixture: FixtureLite; onClick?: () => void }) {
  const isLive = ['1H', '2H', 'HT', 'ET', 'P', 'LIVE'].includes(fixture.status || '')
  const isFinished = fixture.status === 'FT'

  return (
    <button
      className={`gw-fixture-row ${isLive ? 'live' : ''}`}
      onClick={onClick}
      type="button"
    >
      <div className="gw-fixture-teams">
        <div className="gw-fixture-team home">
          {fixture.home_team.crest_url && (
            <img src={fixture.home_team.crest_url} alt="" className="gw-team-crest" />
          )}
          <span className="gw-team-name">{fixture.home_team.name}</span>
        </div>
        <div className="gw-fixture-team away">
          {fixture.away_team.crest_url && (
            <img src={fixture.away_team.crest_url} alt="" className="gw-team-crest" />
          )}
          <span className="gw-team-name">{fixture.away_team.name}</span>
        </div>
      </div>

      <div className="gw-fixture-info">
        {isFinished ? (
          <div className="gw-fixture-score">
            <span>{fixture.home_score}</span>
            <span className="gw-score-separator">-</span>
            <span>{fixture.away_score}</span>
          </div>
        ) : isLive ? (
          <div className="gw-fixture-live">
            <div className="gw-fixture-score">
              <span>{fixture.home_score ?? 0}</span>
              <span className="gw-score-separator">-</span>
              <span>{fixture.away_score ?? 0}</span>
            </div>
            <span className="gw-live-indicator">{fixture.status}</span>
          </div>
        ) : (
          <div className="gw-fixture-time">
            <span className="gw-time">{formatKickoffTime(fixture.kickoff_utc)}</span>
            <span className="gw-date">{formatKickoffDate(fixture.kickoff_utc)}</span>
          </div>
        )}
      </div>
    </button>
  )
}

export function GameweekFixtures({ gameweek, onFixtureClick }: GameweekFixturesProps) {
  if (!gameweek || !gameweek.fixtures || gameweek.fixtures.length === 0) {
    return (
      <div className="gw-fixtures-empty">
        <span>No upcoming fixtures</span>
      </div>
    )
  }

  // Group fixtures by date
  const fixturesByDate = gameweek.fixtures.reduce((acc, fixture) => {
    const dateKey = new Date(fixture.kickoff_utc).toDateString()
    if (!acc[dateKey]) acc[dateKey] = []
    acc[dateKey].push(fixture)
    return acc
  }, {} as Record<string, FixtureLite[]>)

  return (
    <div className="gw-fixtures-container">
      <div className="gw-fixtures-header">
        <span className="gw-fixtures-title">Upcoming Fixtures</span>
        {gameweek.gameweek_name && (
          <span className="gw-fixtures-gameweek">{gameweek.gameweek_name}</span>
        )}
      </div>

      <div className="gw-fixtures-list">
        {Object.entries(fixturesByDate).map(([dateKey, fixtures]) => (
          <div key={dateKey} className="gw-date-group">
            <div className="gw-date-header">
              {formatKickoffDate(fixtures[0].kickoff_utc)}
            </div>
            {fixtures.map((fixture) => (
              <FixtureRow
                key={fixture.id}
                fixture={fixture}
                onClick={() => onFixtureClick?.(fixture)}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}
